import { Alert, Button, Col, Modal, Row, Spinner } from "react-bootstrap";
import {
  WfsBadRequestResponse,
  WfsDataResponse,
  WfsDeferredRequestResponse,
  WfsPaymentRequiredResponse,
  WfsResponseType,
} from "../../../types/Wfs/WfsResponse";
import { OrderItemSummary } from "../../../types/Order/orderItemSummary";
import { StyledModalTitle } from "../../../@ui/StyledModalTitle";
import { useNavigate } from "react-router-dom";
import WfsParametersDisplay from "./WfsParametersDisplay";
import ThemedModal from "../../../@ui/Modal/ThemedModal";
import useBasketService from "../../../hooks/useBasketService";
import { useBasketContext } from "../../../context/Basket/BasketContext";
import { useTenantContext } from "../../../context/Tenant/TenantContext";

export interface ReOrderModalProps {
  isLoading: boolean;
  orderItem: OrderItemSummary;
  wfsParameters: any;
  result?:
    | WfsDataResponse
    | WfsDeferredRequestResponse
    | WfsPaymentRequiredResponse
    | WfsBadRequestResponse;
  onClose: () => void;
}

const ReOrderModal = ({
  isLoading,
  orderItem,
  wfsParameters,
  result,
  onClose,
}: ReOrderModalProps) => {
  const navigate = useNavigate();
  const basketService = useBasketService();
  const { setBasket } = useBasketContext();

  const download = () => {
    const dataResult = result as WfsDataResponse;
    const dataUrl = window.URL.createObjectURL(dataResult.data);
    const tempLink = document.createElement("a");
    tempLink.href = dataUrl;
    tempLink.setAttribute("download", dataResult.fileName);
    tempLink.click();
    navigate("/");
    onClose();
  };

  const navigateToBasket = (id: string) => {
    basketService.AddToBasket(id).then((x) => {
      setBasket(x);
      navigate("/basket");
    });
  };

  const {
    config: { baseCurrency },
  } = useTenantContext();

  return (
    <ThemedModal show={true} centered>
      <Modal.Header className="bg-secondary text-white">
        <StyledModalTitle>Re-ordering Data</StyledModalTitle>
      </Modal.Header>
      <Modal.Body>
        <Row>
          <Col md="3">Product</Col>
          <Col md="9">{orderItem.product}</Col>
        </Row>
        <WfsParametersDisplay
          areaDescription={orderItem.area}
          wfsParameters={wfsParameters}
        />
        <Row>
          <Col md="3">Last Ordered</Col>
          <Col md="9">{orderItem.orderDate.toFormat("dd/MM/yyyy HH:mm")}</Col>
        </Row>
        <hr />
        {!result && isLoading && (
          <Alert>
            <Spinner size="sm" /> Please wait while we re-order your data
          </Alert>
        )}
        {result && result.type === WfsResponseType.Data && (
          <Alert variant="success">Your data is ready!</Alert>
        )}
        {result && result.type === WfsResponseType.DeferredRequest && (
          <Alert variant="success">Your order is queue for processing</Alert>
        )}
        {result && result.type === WfsResponseType.PaymentRequired && (
          <Alert variant="warning">
            This is a Premium dataset and will require additional payment.
          </Alert>
        )}
        {result &&
          result.type === WfsResponseType.PaymentRequired &&
          orderItem.transactionCurrencyCode !== baseCurrency && (
            <Alert variant="warning">
              Due to currency conversion, the price of this order may have
              changed. Please proceed to the basket to review the updated price
              before completing your purchase.
            </Alert>
          )}
        {result && result.type === WfsResponseType.BadRequest && (
          <Alert variant="danger">
            <Alert.Heading>Cannot process your order</Alert.Heading>
            {(result as WfsBadRequestResponse).error}
          </Alert>
        )}
      </Modal.Body>
      {result && (
        <Modal.Footer>
          {result && result.type === WfsResponseType.Data && (
            <Button variant="success" onClick={download}>
              Download Data
            </Button>
          )}
          {result &&
            (result.type === WfsResponseType.DeferredRequest ||
              result.type === WfsResponseType.BadRequest) && (
              <Button variant="primary" onClick={onClose}>
                OK
              </Button>
            )}
          {result && result.type === WfsResponseType.PaymentRequired && (
            <Button
              variant="success"
              onClick={() => navigateToBasket(orderItem.orderItemId)}
            >
              Proceed to Basket
            </Button>
          )}
        </Modal.Footer>
      )}
    </ThemedModal>
  );
};

export default ReOrderModal;
